/**
 * AdminQuotesDashboard.jsx — Dashboard admin type "mini-CRM premium"
 *
 * Affiche la liste des demandes de devis entreprise avec filtres,
 * priorités, propriétaires, next actions, et badges de statut.
 *
 * API : GET /api/admin/enterprise/quote-requests
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';
import { toast } from 'sonner';
import {
  Building2, FileText, Filter, Search, ChevronRight,
  Flame, AlertTriangle, MessageCircle, Clock, User, Tag,
  ArrowLeft, Trash2,
} from 'lucide-react';

const API = getApiUrl();

const STATUS_META = {
  pending_review: { label: 'En analyse', color: '#D97706', bg: '#FEF3C7' },
  quoted: { label: 'Devis émis', color: '#047857', bg: '#D1FAE5' },
  rejected: { label: 'Refusée', color: '#991B1B', bg: '#FEE2E2' },
  cancelled: { label: 'Annulée', color: '#6B7280', bg: '#F3F4F6' },
};
const PRIORITY_META = {
  urgent: { label: 'Urgente', color: '#991B1B', bg: '#FEE2E2', icon: Flame },
  high: { label: 'Élevée', color: '#D97706', bg: '#FEF3C7', icon: AlertTriangle },
  normal: { label: 'Normale', color: '#4B5563', bg: '#F3F4F6', icon: Tag },
  low: { label: 'Basse', color: '#6B7280', bg: '#F9FAFB', icon: Tag },
};

export default function AdminQuotesDashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', type: '', priority: '', owner: '', q: '' });

  useEffect(() => {
    fetchItems();
  }, [filters]); // eslint-disable-line

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await fetch(`${API}/admin/enterprise/quote-requests?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Erreur API');
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      toast.error(err.message);
    } finally { setLoading(false); }
  };

  const stats = useMemo(() => ({
    total: items.length,
    pending: items.filter(i => i.status === 'pending_review').length,
    quoted: items.filter(i => i.status === 'quoted').length,
    urgent: items.filter(i => i.commercial_priority === 'urgent').length,
  }), [items]);

  return (
    <div className="theme-light min-h-screen bg-[#FBF9F3] pb-20">
      <div className="border-b border-[#E8E5DE] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <button onClick={() => navigate('/admin')}
            data-testid="admin-quotes-back"
            className="inline-flex items-center gap-1.5 text-[#4B5563] hover:text-[#111827] text-sm mb-4 px-3 py-1.5 rounded-full bg-[#F8F7F4] border border-[#E8E5DE] hover:bg-[#EBEAE3] transition">
            <ArrowLeft className="w-4 h-4" /> Retour au dashboard
          </button>
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <p className="uppercase text-[11px] tracking-[0.25em] text-[#92710A] font-medium">Administration — CRM Entreprise</p>
            <Link to="/admin/enterprise-orders"
              className="text-[11px] text-[#4B5563] hover:text-[#92710A] underline"
              data-testid="admin-quotes-goto-orders">
              → Commandes & facturation
            </Link>
          </div>
          <h1 className="text-2xl sm:text-3xl font-display font-medium text-[#111827] tracking-tight">Demandes de devis</h1>
          <p className="text-sm text-[#4B5563] mt-1">Pilotez le cycle commercial de vos prospects entreprise.</p>

          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            <KpiCard label="Total" value={stats.total} accent="#111827" />
            <KpiCard label="En analyse" value={stats.pending} accent="#D97706" />
            <KpiCard label="Devis émis" value={stats.quoted} accent="#047857" />
            <KpiCard label="Urgentes" value={stats.urgent} accent="#991B1B" />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Filters */}
        <div className="bg-white border border-[#E8E5DE] rounded-2xl p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="admin-quotes-filters">
          <div className="flex-1 min-w-[220px] relative">
            <Search className="w-4 h-4 text-[#6B7280] absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" placeholder="Entreprise, email, ID…" value={filters.q}
              onChange={(e) => setFilters(f => ({ ...f, q: e.target.value }))}
              data-testid="admin-quotes-search"
              className="w-full pl-9 pr-3 py-2 bg-[#F8F7F4] border border-[#E8E5DE] rounded-xl text-sm text-[#111827] focus:outline-none focus:border-[#92710A]" />
          </div>
          <FilterSelect testid="filter-status" value={filters.status} onChange={(v) => setFilters(f => ({ ...f, status: v }))}
            options={[['', 'Tous statuts'], ['pending_review', 'En analyse'], ['quoted', 'Devis émis'], ['rejected', 'Refusée'], ['cancelled', 'Annulée']]} />
          <FilterSelect testid="filter-type" value={filters.type} onChange={(v) => setFilters(f => ({ ...f, type: v }))}
            options={[['', 'Tous types'], ['ia', 'Coaching IA'], ['human', 'Coaching Humain']]} />
          <FilterSelect testid="filter-priority" value={filters.priority} onChange={(v) => setFilters(f => ({ ...f, priority: v }))}
            options={[['', 'Toutes priorités'], ['urgent', 'Urgente'], ['high', 'Élevée'], ['normal', 'Normale'], ['low', 'Basse']]} />
          <FilterSelect testid="filter-owner" value={filters.owner} onChange={(v) => setFilters(f => ({ ...f, owner: v }))}
            options={[['', 'Tous propriétaires'], ['me', 'Mes dossiers'], ['unassigned', 'Non assigné']]} />
        </div>

        {/* List */}
        {loading ? (
          <div className="text-sm text-[#6B7280] py-10 text-center">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-[#E8E5DE] rounded-2xl p-10 text-center text-[#6B7280] text-sm">
            Aucune demande ne correspond aux filtres.
          </div>
        ) : (
          <div className="space-y-2" data-testid="admin-quotes-list">
            {items.map((item, i) => (
              <QuoteRow key={item.id} item={item} delay={i * 0.02}
                onDeleted={() => setItems(cur => cur.filter(x => x.id !== item.id))}
                token={token} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, accent }) {
  return (
    <div className="bg-white border border-[#E8E5DE] rounded-xl p-4">
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-medium">{label}</div>
      <div className="text-2xl font-display mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function FilterSelect({ value, onChange, options, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="px-3 py-2 bg-white border border-[#E8E5DE] rounded-xl text-sm text-[#111827] focus:outline-none focus:border-[#92710A]">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function QuoteRow({ item, delay, onDeleted, token }) {
  const status = STATUS_META[item.status] || { label: item.status, color: '#6B7280', bg: '#F3F4F6' };
  const priority = PRIORITY_META[item.commercial_priority] || PRIORITY_META.normal;
  const PrioIcon = priority.icon;
  const createdAt = item.created_at ? new Date(item.created_at).toLocaleDateString('fr-FR') : '—';
  const typeLabel = item.type === 'ia' ? 'Coaching IA' : 'Coaching Humain';
  const contact = item.contact || {};
  const payload = item.payload || {};

  const handleDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const company = contact.company_name || item.request_number || item.id;
    if (!window.confirm(
      `Supprimer la demande de devis de "${company}" ?\n\n` +
      `• Suppression logique (soft delete)\n` +
      `• La demande ne sera plus visible dans la liste\n` +
      `• Les commandes liées ne sont PAS affectées\n` +
      `• Action tracée dans l'audit log admin`
    )) return;
    try {
      const res = await fetch(`${getApiUrl()}/admin/enterprise/quote-requests/${item.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.message || `HTTP ${res.status}`);
      }
      toast.success('Demande supprimée');
      onDeleted && onDeleted();
    } catch (err) {
      toast.error(`Échec : ${err.message}`);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      className="relative group">
      <Link to={`/admin/enterprise-quotes/${item.id}`} data-testid={`admin-quote-row-${item.id}`}
        className="block bg-white border border-[#E8E5DE] rounded-2xl p-4 pr-14 hover:border-[#92710A] transition-all">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-[#FBF9F3] border border-[#E8DFC8] flex items-center justify-center flex-shrink-0">
            <Building2 className="w-5 h-5 text-[#92710A]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-[#111827] font-medium text-sm truncate">{contact.company_name || '—'}</h3>
              {item.request_number && (
                <span className="text-[10px] font-mono text-[#4B5563] bg-[#FBF9F3] border border-[#E8DFC8] px-2 py-0.5 rounded-full">
                  {item.request_number}
                </span>
              )}
              <span className="text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full"
                style={{ color: status.color, backgroundColor: status.bg }}>
                {status.label}
              </span>
              <span className="text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                style={{ color: priority.color, backgroundColor: priority.bg }}>
                <PrioIcon className="w-3 h-3" /> {priority.label}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-[#4B5563] bg-[#F8F7F4] px-2 py-0.5 rounded-full">
                {typeLabel}
              </span>
            </div>
            <div className="flex items-center gap-4 text-[11px] text-[#6B7280] mt-1.5 flex-wrap">
              <span>{contact.hr_contact_email}</span>
              <span>·</span>
              <span>{payload.collaborators_count || '—'} collaborateurs</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {createdAt}</span>
              {item.internal_owner_id && (
                <span className="inline-flex items-center gap-1 text-[#92710A]">
                  <User className="w-3 h-3" /> assigné
                </span>
              )}
              {item.comments_count > 0 && (
                <span className="inline-flex items-center gap-1">
                  <MessageCircle className="w-3 h-3" /> {item.comments_count}
                </span>
              )}
              {item.latest_quote && (
                <span className="inline-flex items-center gap-1 text-[#047857] font-medium">
                  <FileText className="w-3 h-3" /> {item.latest_quote.quote_number}
                </span>
              )}
            </div>
            {item.next_action && (
              <div className="mt-2 text-[11px] text-[#92710A] inline-flex items-center gap-1.5">
                <Filter className="w-3 h-3" /> Prochaine action : <span className="font-medium">{item.next_action}</span>
              </div>
            )}
          </div>
          <ChevronRight className="w-5 h-5 text-[#6B7280] group-hover:text-[#92710A] flex-shrink-0" />
        </div>
      </Link>
      <button onClick={handleDelete}
        data-testid={`delete-quote-${item.id}`}
        title="Supprimer la demande"
        aria-label="Supprimer la demande"
        className="absolute top-2 right-2 z-10 p-2 rounded-lg bg-white border border-[#FECACA] text-[#991B1B] shadow-sm hover:bg-[#FEE2E2] hover:border-[#EF4444] active:scale-95 transition-all min-w-[40px] min-h-[40px] flex items-center justify-center">
        <Trash2 className="w-4 h-4" />
      </button>
    </motion.div>
  );
}
